import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { REPORTS } from '../../constant';

interface Clip {
  name: string;
  url: string;
  sopInstanceUID: string;
}

const isImageUrl = (url: string) => /\.(png|jpe?g|gif|webp|bmp)(\?|#|$)/i.test(url);

// ── Lightbox ──────────────────────────────────────────────────────────────────

interface LightboxProps {
  clips: Clip[];
  index: number;
  onClose: () => void;
  onNavigate: (i: number) => void;
}

const Lightbox: React.FC<LightboxProps> = ({ clips, index, onClose, onNavigate }) => {
  const clip = clips[index];
  const showImage = isImageUrl(clip.url);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onNavigate((index + 1) % clips.length);
      else if (e.key === 'ArrowLeft') onNavigate((index - 1 + clips.length) % clips.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, clips.length]);

  return (
    <div style={lb.overlay} onClick={onClose}>
      <div style={lb.box} onClick={e => e.stopPropagation()}>
        <button style={lb.close} onClick={onClose}>×</button>
        <p style={lb.name}>{clip.name}</p>

        {showImage ? (
          <img src={clip.url} alt={clip.name} style={lb.media} />
        ) : (
          <video src={clip.url} controls autoPlay loop style={lb.media} />
        )}

        {clips.length > 1 && (
          <>
            <button
              style={{ ...lb.nav, left: 8 }}
              onClick={() => onNavigate((index - 1 + clips.length) % clips.length)}
            >
              ‹
            </button>
            <button
              style={{ ...lb.nav, right: 8 }}
              onClick={() => onNavigate((index + 1) % clips.length)}
            >
              ›
            </button>
          </>
        )}

        <p style={lb.counter}>{index + 1} / {clips.length}</p>
      </div>
    </div>
  );
};

const lb: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
  },
  box: {
    position: 'relative', background: '#0a1e30', borderRadius: 10,
    padding: '12px 12px 40px', maxWidth: '90vw', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  close: {
    position: 'absolute', top: 8, right: 12, background: 'transparent',
    border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', lineHeight: 1,
  },
  name: {
    color: '#cdd5e0', fontSize: 13, marginBottom: 10, alignSelf: 'flex-start',
  },
  media: {
    maxWidth: '80vw', maxHeight: '75vh', borderRadius: 6, background: '#000', display: 'block',
  },
  nav: {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff',
    fontSize: 36, cursor: 'pointer', borderRadius: 6, padding: '4px 10px', lineHeight: 1,
  },
  counter: {
    position: 'absolute', bottom: 10, color: '#8899aa', fontSize: 12,
  },
};

// ── Clip card ─────────────────────────────────────────────────────────────────

interface CardProps {
  clip: Clip;
  onClick: () => void;
}

const ClipCard: React.FC<CardProps> = ({ clip, onClick }) => {
  const [failed, setFailed] = useState(false);
  const [useImage, setUseImage] = useState(isImageUrl(clip.url));

  const handleError = () => {
    if (!failed) { setUseImage(prev => !prev); setFailed(true); }
  };

  return (
    <div style={styles.card} onClick={onClick} title="Click to expand">
      <p style={styles.clipName}>{clip.name}</p>
      {useImage ? (
        <img src={clip.url} alt={clip.name} style={styles.media} onError={handleError} />
      ) : (
        <video src={clip.url} controls loop style={styles.media} onError={handleError} />
      )}
    </div>
  );
};

// ── Main viewer ───────────────────────────────────────────────────────────────

const SharedClipsViewer: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [clips, setClips] = useState<Clip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!uuid) return;
    fetch(REPORTS.GET_PUBLIC_CLIPS(uuid))
      .then(res => res.json())
      .then(data => {
        if (data?.status === 'SUCCESS') setClips(data.data?.clips || []);
        else setError('Clips not found.');
      })
      .catch(() => setError('Failed to load clips.'))
      .finally(() => setLoading(false));
  }, [uuid]);

  if (loading) return <div style={styles.center}><p style={styles.text}>Loading clips...</p></div>;
  if (error || clips.length === 0) return <div style={styles.center}><p style={styles.text}>{error || 'No clips available.'}</p></div>;

  return (
    <div style={styles.page}>
      <div style={styles.grid}>
        {clips.map((clip, i) => (
          <ClipCard key={clip.sopInstanceUID} clip={clip} onClick={() => setLightboxIndex(i)} />
        ))}
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          clips={clips}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { background: '#041527', minHeight: '100vh', padding: '24px' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '16px',
  },
  card: {
    background: '#0a1e30', borderRadius: '8px', padding: '12px',
    cursor: 'pointer', transition: 'transform 0.15s',
  },
  clipName: { color: '#fff', fontSize: '13px', marginBottom: '8px', fontWeight: 500 },
  media: { width: '100%', borderRadius: '4px', background: '#000', display: 'block' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#041527' },
  text: { color: '#fff', fontSize: '16px' },
};

export default SharedClipsViewer;
